// Sekage motion: the physics behind panels you can grab, throw and reverse. Shared by sekage-navigation.js
// (drawers) and sekage-storefront.js (product rails).
// - Springs, not timed animations: a spring starts from wherever the thing is on screen and keeps whatever
//   speed it already has, so a moving panel can be grabbed, redirected or reversed at any instant.
// - damping 1 settles without overshoot (the default); below 1 it overshoots a little, which is kept for
//   motion that follows a flick. response is roughly the seconds it takes to arrive; it is not a duration.
// - project() is the deceleration curve scrolling uses: where a throw would come to rest.
// - rubberband() is the resistance past an edge: it follows less the further it is pulled.
(() => {
  if (window.SekageMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  // One value on a spring. onUpdate(value) every frame; onComplete() once it has settled on `to`.
  // The returned handle reports the live value and velocity, so the next spring can start from them.
  function spring({ from, to, velocity = 0, damping = 1, response = 0.4, onUpdate, onComplete }) {
    const stiffness = (2 * Math.PI / response) ** 2;
    const friction = 4 * Math.PI * damping / response;
    let value = from;
    let speed = velocity; // px per second
    let frame = 0;
    let last = 0;
    let running = true;
    const handle = {
      get value() { return value; },
      get velocity() { return speed; },
      get running() { return running; },
      stop() { running = false; cancelAnimationFrame(frame); },
    };
    if (reduced.matches || from === to && !velocity) {
      value = to; speed = 0; running = false;
      onUpdate?.(to); onComplete?.();
      return handle;
    }
    const step = now => {
      if (!running) return;
      // Fixed 4 ms substeps keep the integration stable however long the frame took.
      let elapsed = last ? Math.min(0.064, (now - last) / 1000) : 1 / 60;
      last = now;
      while (elapsed > 0) {
        const dt = Math.min(elapsed, 0.004);
        speed += (-stiffness * (value - to) - friction * speed) * dt;
        value += speed * dt;
        elapsed -= dt;
      }
      if (Math.abs(value - to) < 0.5 && Math.abs(speed) < 10) {
        value = to; speed = 0; running = false;
        onUpdate?.(to); onComplete?.();
        return;
      }
      onUpdate?.(value);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return handle;
  }

  // Where a throw at `velocity` (px/s) comes to rest. 0.998 is normal scrolling; 0.99 stops sooner.
  const project = (velocity, rate = 0.998) => (velocity / 1000) * rate / (1 - rate);

  // How far something follows when pulled `overshoot` px past its edge, for a surface `dimension` px long.
  const rubberband = (overshoot, dimension, constant = 0.55) =>
    (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

  // Pointer velocity from the last ~100 ms of samples, so a finger that stopped before lifting reads as still.
  function tracker() {
    let samples = [];
    return {
      add(x, y, t = performance.now()) {
        samples.push({ x, y, t });
        samples = samples.filter(s => t - s.t <= 100);
      },
      velocity(now = performance.now()) {
        samples = samples.filter(s => now - s.t <= 100);
        if (samples.length < 2) return { x: 0, y: 0 };
        const first = samples[0];
        const lastSample = samples[samples.length - 1];
        const dt = (lastSample.t - first.t) / 1000;
        if (dt <= 0) return { x: 0, y: 0 };
        return { x: (lastSample.x - first.x) / dt, y: (lastSample.y - first.y) / dt };
      },
      reset() { samples = []; },
    };
  }

  window.SekageMotion = { spring, project, rubberband, tracker, reduced };
})();
